import type { Chapter } from '../types/pulse';

// All peerPercent values are ILLUSTRATIVE DEMONSTRATION DATA — not survey findings.
export const chapters: Chapter[] = [
  {
    id: 'starting-point',
    number: '01',
    numberWord: 'One',
    theme: 'Individualisation',
    title: 'The Starting Point',
    openingThought: 'No two patients are quite the same. But what drives your starting decision?',
    promise: 'Do your peers start from the same place you do?',
    question: 'What most influences your initial stimulation strategy?',
    options: [
      { id: 'reserve', label: 'Ovarian reserve markers', short: 'Ovarian reserve markers', peerPercent: 38 },
      { id: 'history', label: 'Previous response to stimulation', short: 'Previous response', peerPercent: 29 },
      { id: 'age', label: 'Patient age', short: 'Patient age', peerPercent: 21 },
      { id: 'weight', label: 'Body weight', short: 'Body weight', peerPercent: 12 },
    ],
    followUps: {
      reserve: {
        question: 'Why do ovarian reserve markers carry the most weight for you?',
        options: [
          { id: 'objective', label: 'They offer the most objective starting data', peerPercent: 34 },
          { id: 'predictive', label: 'They best anticipate response in my experience', peerPercent: 31 },
          { id: 'extremes', label: 'They help anticipate risk at both extremes', peerPercent: 23 },
          { id: 'consistency', label: 'They support consistent decisions across the team', peerPercent: 12 },
        ],
      },
      history: {
        question: 'Why does previous response matter most to you?',
        options: [
          { id: 'real', label: 'It is real-world evidence for this patient', peerPercent: 41 },
          { id: 'unexpected', label: 'It reveals unexpected over- or under-response', peerPercent: 26 },
          { id: 'adjust', label: 'It guides adjustments for the next cycle', peerPercent: 22 },
          { id: 'uncertainty', label: 'It reduces uncertainty versus prediction alone', peerPercent: 11 },
        ],
      },
      age: {
        question: 'Why does age carry the greatest weight for you?',
        options: [
          { id: 'quality', label: 'It reflects oocyte quality, not just quantity', peerPercent: 44 },
          { id: 'goals', label: 'It shapes the overall treatment goals', peerPercent: 24 },
          { id: 'time', label: 'It influences how much time is available', peerPercent: 19 },
          { id: 'counsel', label: 'It guides counselling and expectations', peerPercent: 13 },
        ],
      },
      weight: {
        question: 'Why does body weight weigh most in your decision?',
        options: [
          { id: 'dosing', label: 'It affects how patients respond to dosing', peerPercent: 39 },
          { id: 'finetune', label: 'It helps fine-tune the starting dose', peerPercent: 28 },
          { id: 'risk', label: 'It relates to risk considerations', peerPercent: 21 },
          { id: 'health', label: 'It reflects overall patient health', peerPercent: 12 },
        ],
      },
    },
    rewardHeadline: 'Your starting point, among your peers’ priorities',
    insight: 'Individualisation begins with understanding which differences matter.',
    visual: 'variability',
    visualCaption: 'Illustrative visualisation · Patient variability',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b15579cb-8974-4c01-823e-6f854388583d.jpg',
    accent: '#0F9C8C',
    openingLayout: 'top',
  },
  {
    id: 'balancing-act',
    number: '02',
    numberWord: 'Two',
    theme: 'Ovarian response',
    title: 'The Balancing Act',
    openingThought: 'Every stimulation strategy involves balancing competing priorities.',
    promise: 'Where do fertility specialists disagree most?',
    question: 'What’s the hardest balance to strike in ovarian stimulation?',
    options: [
      { id: 'excessive', label: 'Reducing the risk of excessive response', short: 'Avoiding excessive response', peerPercent: 31 },
      { id: 'appropriate', label: 'Achieving an appropriate ovarian response', short: 'An appropriate response', peerPercent: 27 },
      { id: 'uncertainty', label: 'Managing uncertainty between patients', short: 'Managing uncertainty', peerPercent: 24 },
      { id: 'insufficient', label: 'Avoiding insufficient response', short: 'Avoiding insufficient response', peerPercent: 18 },
    ],
    followUps: {
      appropriate: {
        question: 'What makes an appropriate response hardest to achieve?',
        options: [
          { id: 'define', label: 'Defining “appropriate” for each patient', peerPercent: 33 },
          { id: 'translate', label: 'Translating predictions into a dose', peerPercent: 29 },
          { id: 'during', label: 'Adjusting course during stimulation', peerPercent: 21 },
          { id: 'balance', label: 'Weighing yield against safety', peerPercent: 17 },
        ],
      },
      excessive: {
        question: 'What makes excessive response particularly challenging in your practice?',
        options: [
          { id: 'consequences', label: 'Clinical consequences for the patient', peerPercent: 42 },
          { id: 'disruption', label: 'Treatment disruption or additional interventions', peerPercent: 24 },
          { id: 'identify', label: 'Difficulty identifying patients at risk', peerPercent: 22 },
          { id: 'monitoring', label: 'Monitoring and resource requirements', peerPercent: 12 },
        ],
      },
      insufficient: {
        question: 'What’s your greatest concern when ovarian response is insufficient?',
        options: [
          { id: 'fewer', label: 'Fewer oocytes than anticipated', peerPercent: 36 },
          { id: 'progression', label: 'Potential impact on treatment progression', peerPercent: 27 },
          { id: 'expectations', label: 'Managing patient expectations', peerPercent: 22 },
          { id: 'adjustment', label: 'Need for treatment adjustment', peerPercent: 15 },
        ],
      },
      uncertainty: {
        question: 'What contributes most to that uncertainty?',
        options: [
          { id: 'variability', label: 'Variability in patient characteristics', peerPercent: 35 },
          { id: 'markers', label: 'Limitations of available predictive markers', peerPercent: 28 },
          { id: 'gap', label: 'Differences between predicted and observed response', peerPercent: 25 },
          { id: 'history', label: 'Previous treatment history', peerPercent: 12 },
        ],
      },
    },
    rewardHeadline: 'Where your balance sits',
    insight: 'Predicting response is one challenge. Balancing competing priorities is another.',
    visual: 'follicles',
    visualCaption: 'Illustrative visualisation · Follicular development',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1c3a9fb1-becb-4dbc-b05b-9af88421d84a.jpg',
    accent: '#D9654F',
    openingLayout: 'full',
  },
  {
    id: 'next-frontier',
    number: '03',
    numberWord: 'Three',
    theme: 'Future improvement',
    title: 'The Next Frontier',
    openingThought: 'If you could make one part of ovarian stimulation better, where would you start?',
    promise: 'Which simplification would your peers choose first?',
    question: 'Where could ovarian stimulation become simpler?',
    options: [
      { id: 'monitoring', label: 'Monitoring during the cycle', short: 'Monitoring', peerPercent: 34 },
      { id: 'dose', label: 'Selecting the starting dose', short: 'Dose selection', peerPercent: 26 },
      { id: 'adjust', label: 'Adjusting treatment mid-cycle', short: 'In-cycle adjustments', peerPercent: 25 },
      { id: 'selfadmin', label: 'Patient self-administration', short: 'Self-administration', peerPercent: 15 },
    ],
    followUps: {
      monitoring: {
        question: 'What makes monitoring most burdensome today?',
        options: [
          { id: 'visits', label: 'Number of clinic visits for patients', peerPercent: 40 },
          { id: 'capacity', label: 'Staff time and clinic capacity', peerPercent: 29 },
          { id: 'interpret', label: 'Interpreting results under time pressure', peerPercent: 18 },
          { id: 'cost', label: 'Cost to patients or the system', peerPercent: 13 },
        ],
      },
      dose: {
        question: 'Why would a simpler dose decision matter most?',
        options: [
          { id: 'course', label: 'It sets the course for the whole cycle', peerPercent: 37 },
          { id: 'fewer', label: 'Starting right reduces later changes', peerPercent: 30 },
          { id: 'capture', label: 'Patients differ in ways hard to capture', peerPercent: 19 },
          { id: 'vary', label: 'Approaches vary between clinicians', peerPercent: 14 },
        ],
      },
      adjust: {
        question: 'What makes in-cycle adjustments complex?',
        options: [
          { id: 'when', label: 'Deciding when to adjust', peerPercent: 33 },
          { id: 'howmuch', label: 'Deciding how much to adjust', peerPercent: 31 },
          { id: 'impact', label: 'Uncertainty about the impact', peerPercent: 22 },
          { id: 'communicate', label: 'Communicating changes to patients', peerPercent: 14 },
        ],
      },
      selfadmin: {
        question: 'What’s the main challenge with self-administration?',
        options: [
          { id: 'confidence', label: 'Patient anxiety and confidence', peerPercent: 38 },
          { id: 'steps', label: 'Number of preparation steps', peerPercent: 27 },
          { id: 'errors', label: 'Risk of dosing errors or confusion', peerPercent: 21 },
          { id: 'training', label: 'Time needed to train patients', peerPercent: 14 },
        ],
      },
    },
    rewardHeadline: 'Where specialists see opportunity',
    insight: 'Where specialists feel friction today often signals where the field will look next.',
    visual: 'lattice',
    visualCaption: 'Illustrative visualisation · The next frontier',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/3915790b-5229-4347-99a8-f39000f6fac7.jpg',
    accent: '#5468D8',
    openingLayout: 'framed',
  },
];

export const media = {
  intro: 'https://cdn.magicpatterns.com/patterns/generated-images/2a6cac59-5e1b-496a-902e-b14bce1b36b7.jpg',
  finale: 'https://cdn.magicpatterns.com/patterns/generated-images/d806809e-33b0-4ba4-b1bf-b16344d83652.jpg',
};
